import type {
  DeviceSync,
  ResolvedSlot,
  ScheduleBoundary,
} from "@workspace/api-client-react";

/**
 * Screen-timeline consumption (CB-024/CB-028). `/device/sync` now carries
 * `screenTimeline`: every screen-bus window in the sync horizon as a
 * ResolvedSlot (signage hydrated), produced by the same composed arbitration
 * timeline as the plan. The player derives what the video channel shows from
 * `screenSlotAt(sync, now)` (player-core), and these helpers turn the windows
 * into boundary-shaped entries so the BoundaryScheduler re-syncs the screen
 * bus at every transition — mid-window signage changes included — instead of
 * freezing on the poll-time now/next snapshot.
 */

/** The screen timeline's windows as BoundaryScheduler-armable entries. */
export function screenTimelineBoundaries(
  sync: DeviceSync | null,
): ScheduleBoundary[] {
  const timeline = sync?.screenTimeline;
  if (!Array.isArray(timeline)) return [];
  const out: ScheduleBoundary[] = [];
  for (const slot of timeline) {
    if (!slot.startsAt || !slot.endsAt) continue;
    out.push({
      channelType: slot.channelType,
      startsAt: slot.startsAt,
      endsAt: slot.endsAt,
      source: slot.source,
      sourceId: slot.sourceId ?? null,
    });
  }
  return out;
}

/**
 * The boundaries to arm for a sync payload. When the payload carries a
 * `screenTimeline` its windows REPLACE the video-channel base boundaries —
 * the timeline is a superset (base and timed windows alike), so each screen
 * transition fires exactly once. Payloads without the field keep the plain
 * boundary list (pre-upgrade offline caches).
 */
export function boundariesWithScreenTimeline(
  sync: DeviceSync | null,
): ScheduleBoundary[] {
  const boundaries = sync?.boundaries ?? [];
  if (!Array.isArray(sync?.screenTimeline)) return boundaries;
  return [
    ...boundaries.filter((b) => b.channelType !== "video"),
    ...screenTimelineBoundaries(sync),
  ];
}

/** Convenience for tests/UI: the timeline windows that reference signage. */
export function screenTimelineSignageSlots(
  sync: DeviceSync | null,
): ResolvedSlot[] {
  const timeline = sync?.screenTimeline;
  if (!Array.isArray(timeline)) return [];
  return timeline.filter((slot) => Boolean(slot.signageContentId));
}
